import React, { Component } from 'react';
import { QueryRenderer } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';
import { Table, TableHead, TableCell, TableBody, TableRow, TableSortLabel, Typography, Tooltip } from '@mui/material';
import { Button, FormControl, FormControlLabel, Grid, InputLabel, Link, MenuItem, Select, Paper, Switch } from '@mui/material';
import { TablePagination } from '@mui/material';
import Add from '@mui/icons-material/Add';
import ReportProblem from '@mui/icons-material/ReportProblem';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import PendingActions from '@mui/icons-material/PendingActions';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';


import PageTitle from '../ui/PageTitle';
import ServerError from '../ui/ServerError';
import LoadingSpinner from '../ui/LoadingSpinner';
//import SearchField from '../ui/SearchField';
import SearchFieldMedium from '../ui/SearchFieldMedium';
// import SearchFieldNarrow from '../ui/SearchFieldNarrow';
import AppBindStyles from '../../AppBindStyles';

import CheckCircle from '@mui/icons-material/CheckCircle';
import BuildCircle from '@mui/icons-material/BuildCircle';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';
import Cancel from '@mui/icons-material/Cancel';

import { green, red, amber, grey } from '@mui/material/colors';


const abEnvironment = new AbEnvironment();

const CustomerSearchTableQuery = graphql`
query CustomerSearchTableQuery($resellerAccountId: UUID!, $accountName: String!, $accountNumber: String!, $activeOnly: Int,  $floatProblemOnly: Int, $outstandingSOOnly: Int, $orderBy: Int, $direction: Int, $first: Int, $offset: Int) {
  accountsearchforcustomer4(reselleraccountid: $resellerAccountId, accountname: $accountName, accountnumber: $accountNumber, activeonly: $activeOnly, floatproblemonly: $floatProblemOnly, outstandingsoonly: $outstandingSOOnly, orderby: $orderBy, direction: $direction, first: $first, offset: $offset) {
    totalCount
    nodes {
      accountId
      accountName
      accountNum
      contactLastName
      contactFirstName
      contactEmail
      contactPhone
      accountStatus
      customerStatus
      floatProblem
      declineCount
      lastDeclineReason
      lastDeclineChargeAmount
      soStatus
      connectedEntities {
        accountIntegrationId
        appName
        appLogo
        appLogoWide
        entity
        entityId
      }
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
    //marginTop: 10,
    justifyContent: 'flex-end',
    backgroundColor: theme.palette.background.default,
  },
  flex: {
    flex: 1,
  },
  paper: {
    maxWidth: 1200,
    marginTop: '16px'
  },
  radio: {
    marginTop: 16
  },
  formControl: {
    marginTop: '0',
  },
  nselect: {
    minWidth: 150
  }
});

class CustomerSearchTable extends Component {
  state = {
    customerName: '',
    customerNum: '',
    activeOnly: 1,
    floatProblemOnly: 0,
    outstandingSOOnly: 0,
    orderBy: 0,
    orderDir: 1,
    page: 0,
    rowsPerPage: 10,
    mountComplete: 0
  };

  componentDidMount() {
    if (abEnvironment.isSegmentActive()) { window.analytics.page(); }

    const params = new URLSearchParams(this.props.location.search);
    let sTerm = params.get('sterm');
    if (sTerm !== null) {
      //backward compatibility if any
      this.setState({customerName: sTerm, mountComplete: 1});
    } else {
       // Is there something in local storage?
       sTerm = localStorage.getItem('customersSterms');
       if (sTerm !== null) {

        try {
          var searchTerms = JSON.parse(sTerm);
          this.setState({customerName: searchTerms.customerName, customerNum: searchTerms.customerNum, activeOnly: searchTerms.activeOnly, floatProblemOnly: searchTerms.floatProblemOnly, orderBy: searchTerms.orderBy, mountComplete: 1});
        } catch (error) {
          //console.log(error);
        }

      } else {
        this.setState({mountComplete: 1});
      }
    }
  }

  goTo(route) {
    var searchToSave = {customerName: this.state.customerName, customerNum: this.state.customerNum, activeOnly: this.state.activeOnly, floatProblemOnly: this.state.floatProblemOnly, orderBy: this.state.orderBy}
    var searchTerms = JSON.stringify(searchToSave);
    localStorage.setItem('customersSterms', searchTerms);
    this.props.history.push(`/${route}`);
  }

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
    });
  };

  handleChangePage = (event, page) => {
    this.setState({page: page});
  }
  
  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page : 0});
  }

  toggleOrder = (ob) => {
    let newDir = 0;
    if (ob === this.state.orderBy) {
      if (this.state.orderDir === 0) {
        newDir = 1;
      }
    } else {
      newDir = 1;
    }

    this.setOrderBy(ob, newDir);
  } 

  setOrderBy = (ob, dir) => {
    let thedir = dir;
    if (dir === 'asc') {
      thedir = 1;
    } else if (dir === 'desc') {
      thedir = 0;
    }
    if (thedir !== 0 && thedir !== 1) {
      thedir = 1;
    }
    this.setState({orderBy: ob, orderDir: thedir, page: 0});
  }

  getAccountName = (customerName) => {

    var limit = 30;  //seems to be a good general fit
    if (customerName.length > limit) {
      // Find the space before that.
      let theSubstr = customerName.substring(0, limit);
      let lastSpaceIdx = theSubstr.lastIndexOf(' ');
      return customerName.substring(0, lastSpaceIdx) + '...';
    } else {
      return customerName;
    }
  }

  render() {
    const { isAuthenticated, isAuthenticatedReseller, isAccountSuspended, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    const statusOk = green[300];
    const statusSuspended = red[300];
    const statusCancelled = red[300];
    const statusCaution = amber[400];
    const statusBuild = grey[500];

    // Only show the funding switch if expensing is active
    let expensingActive = false;
    let expensing = abEnvironment.getFlagValue("expensing");
    if (expensing != null && expensing.flagValue === 1) {
      expensingActive = true;
    }          

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0)
    {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;
  
    let totalCount = 0;

    if (!isAuthenticated() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    } else if (isAuthenticatedReseller()) {

      let accountId = abEnvironment.getUserAttribute('a_id');

      // Check if there is a FreshBooks integration active.
      let freshBooksActive = false;
      let integratedApps = abEnvironment.getUserAttribute('a_integrations');
      if (integratedApps != null && (integratedApps === 'FreshBooks' || integratedApps.includes('FreshBooks'))) {
        // Show a button
        freshBooksActive = true;
      }

      if (this.state.mountComplete === 1) {
        var searchToSave = {customerName: this.state.customerName, customerNum: this.state.customerNum, activeOnly: this.state.activeOnly, floatProblemOnly: this.state.floatProblemOnly, orderBy: this.state.orderBy}
        var searchTerms = JSON.stringify(searchToSave);
        localStorage.setItem('customersSterms', searchTerms);
      }

      let theDir = 'asc';
      if (this.state.orderDir === 0) {
        theDir = 'desc';
      }

      let showOrderSearch = true;
      if (abEnvironment.getUserAttribute('a_useBudgets') === '0') {
        showOrderSearch = false;
      }

      return (
        <main>
          <PageTitle title="Customers" subtitle="Create and manage app subscriptions for your customers."/>
          <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="end" style={{marginTop: '-24px'}}>
            <Grid item xs={12}>
              <Grid container spacing={1} justifyContent="flex-start">
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={isAccountSuspended()}
                    style={AppBindStyles.addButtonOnSearch}
                    onClick={() => {this.goTo('customercreate');}}
                  >
                    <Add/> Add Customer
                  </Button>
                </Grid>
                {freshBooksActive === true &&
                <Grid item>
                  <Button
                    color="primary"
                    variant="outlined"
                    disabled={isAccountSuspended()}
                    style={AppBindStyles.addButtonOnSearch}
                    onClick={() => {this.goTo('customerimport');}}
                  >
                    <Add/>Import from FreshBooks
                  </Button>
                </Grid>}
              </Grid>
            </Grid>

            <Grid item>
              <SearchFieldMedium
                id="customer-name-search"
                label="Search by customer name"
                value={this.state.customerName}
                onChange={this.handleChange('customerName')}
              />
            </Grid>
            <Grid item>
              <SearchFieldMedium
                  id="customer-num-search"
                  label="Search by customer identifier"
                  value={this.state.customerNum}
                  onChange={this.handleChange('customerNum')}
                />
            </Grid>   
            {showOrderSearch &&
            <Grid item>
              <FormControl variant="standard">
                <InputLabel shrink htmlFor="outstandingSO"></InputLabel>
                <Select
                  value={this.state.outstandingSOOnly}
                  inputProps={{
                    name: 'outstandingSOOnly',
                    id: 'outstandingSOOnly',
                  }}
                  onChange={(e) => { this.setState({ outstandingSOOnly: e.target.value, floatProblemOnly: 0, activeOnly: 1, page: 0 }); } }
                  className={classes.nselect}
                >
                  <MenuItem value={0}>Any order status</MenuItem>
                  <MenuItem value={4}>No order sent</MenuItem>
                  <MenuItem value={1}>Change order needed</MenuItem>
                  <MenuItem value={2}>Order sent</MenuItem>
                  <MenuItem value={3}>Order accepted</MenuItem>
                </Select>
              </FormControl>
            </Grid>}
            <Grid item style={{marginBottom: -4}}>
              <FormControlLabel
                control={
                  <Switch
                    checked={this.state.activeOnly !== 1}
                    onChange={(e, checked) => {
                      if (this.state.activeOnly === 1) {
                        //console.log('setting state to 0');
                        this.setState({ activeOnly: 0, outstandingSOOnly: 0, page: 0});
                      } else {
                        //console.log('setting state to 1');
                        this.setState({ activeOnly: 1, outstandingSOOnly: 0, page: 0});
                      }
                    }}
                  />
                }
                label="Show inactive"
              />
            </Grid>
            {expensingActive === true &&
            <Grid item style={{marginBottom: -4}}>
              <FormControlLabel
                control={
                  <Switch
                    checked={this.state.floatProblemOnly === 1}
                    onChange={(e, checked) => {
                      if (this.state.floatProblemOnly === 1) {
                        //console.log('setting state to 0');
                        this.setState({ floatProblemOnly: 0, outstandingSOOnly: 0, page: 0});
                      } else {
                        //console.log('setting state to 1');
                        this.setState({ floatProblemOnly: 1, outstandingSOOnly: 0, page: 0});
                      }
                    }}
                  />
                }
                label="Funding issues only"
              />
            </Grid>}
          </Grid>
          <QueryRenderer
            environment={abEnvironment.getEnvironment()}
            query={CustomerSearchTableQuery}
            variables={{resellerAccountId: accountId,
                        accountName: this.state.customerName,
                        accountNumber: this.state.customerNum,
                        activeOnly: this.state.activeOnly,
                        floatProblemOnly: this.state.floatProblemOnly,
                        outstandingSOOnly: this.state.outstandingSOOnly,
                        orderBy: this.state.orderBy,
                        direction: this.state.orderDir,
                        first: first,
                        offset: offset}}
            render={({error, props})=> {
              if (error) {
                logError(error.message);
                return (
                  <ServerError/>
                );
              } else if (props) {
                // Determine offset information
                totalCount = props.accountsearchforcustomer4.totalCount;

                let customers = props.accountsearchforcustomer4.nodes;

                // Are there any customers with integration information?
                let integrationCount = 0;
                for (let i=0; i<customers.length; i++) {
                  let aCust = customers[i];
                  if (aCust.connectedEntities != null) {
                    integrationCount = integrationCount + 1;
                  }
                }

                return ( 
                  <div className={classes.paper}>
                    <Paper >
                      <Table>
                          <TableHead style={AppBindStyles.tableHead}>
                              <TableRow style={AppBindStyles.tableHeadRow}>
                                  <TableCell>
                                    <TableSortLabel
                                      active={this.state.orderBy === 0}
                                      direction={theDir}
                                      onClick={() => this.toggleOrder(0) }>
                                      Account name
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell>
                                    <TableSortLabel
                                      active={this.state.orderBy === 1}
                                      direction={theDir}
                                      onClick={() => this.toggleOrder(1) }>
                                      Internal identifier
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell>Contact</TableCell>
                                  <TableCell>Email</TableCell>
                                  {integrationCount > 0 &&
                                  <TableCell>Connections</TableCell>}
                                  <TableCell align="center">Status</TableCell>
                             </TableRow>
                          </TableHead>
                          <TableBody>
                              {customers.map(n => {
                                  let aUrl = "customer?accountId=" + n.accountId;
                                  return (
                                      <TableRow key={n.accountId}>
                                          <TableCell>
                                            <Link component="button" 
                                                  variant="body2"
                                                  className={classes.custlink}
                                                  onClick={this.goTo.bind(this, aUrl)}>
                                              {n.accountName}
                                            </Link>
                                          </TableCell>
                                          <TableCell align="left">{n.accountNum}</TableCell>
                                          <TableCell align="left">{n.contactFirstName}&nbsp;{n.contactLastName}</TableCell>
                                          <TableCell align="left">{n.contactEmail}</TableCell>
                                          {integrationCount > 0 && n.connectedEntities != null &&
                                          <TableCell style={{padding: "8px"}}>
                                            <Grid container justifyContent="center">
                                              {n.connectedEntities.map(m => {
                                                return (
                                                  <Grid item key={m.accountIntegrationId}>
                                                    <Tooltip title={m.appName} aria-label={m.appName} placement="right">
                                                      <img alt={m.appName} src={m.appLogo} height="32px"/>
                                                    </Tooltip>
                                                  </Grid>
                                                );
                                              })}
                                            </Grid>
                                          </TableCell>}
                                          {integrationCount > 0 && n.connectedEntities == null &&
                                          <TableCell/>}
                                          <TableCell align="center">
                                            { n.accountStatus === 0 && n.customerStatus !== 2 && n.floatProblem === 0 && n.declineCount === 0 && n.soStatus !== 1 && n.soStatus !== 2 && n.soStatus !== 0 &&
                                            
                                                <CheckCircle htmlColor={statusOk}/>
                                            
                                            }
                                            { n.accountStatus === 0 && n.customerStatus === 2 &&
                                            
                                            <TransferWithinAStationIcon htmlColor={statusOk}/>
                                        
                                            }
                                            { n.accountStatus === 0 && n.customerStatus !== 2 && n.floatProblem === 0 && n.declineCount > 0 &&
                                            
                                              <Tooltip title="This customer has funding issues">
                                                  <ReportProblem htmlColor={statusCaution}/>
                                              </Tooltip>
                                        
                                            }
                                            { n.accountStatus === 0 && n.customerStatus !== 2 && n.floatProblem === 1 && n.declineCount === 0 &&
                                            
                                            <Tooltip title="Your customer has exceeded their float limit.  Please make sure that they have a valid chargeable payment source.">
                                                <ReportProblem htmlColor={statusCaution}/>
                                            </Tooltip>
                                      
                                          }
                                           { n.accountStatus === 0 && n.customerStatus !== 2 && n.floatProblem === 1 && n.declineCount > 0 &&
                                            
                                            <Tooltip title="This customer has funding issues.">
                                                <ReportProblem htmlColor={statusCaution}/>
                                            </Tooltip>
                                      
                                          }
                                          { n.accountStatus === 0 && n.customerStatus !== 2 && !(n.floatProblem === 1 || n.declineCount > 0) && n.soStatus === 1 &&
                                            
                                            <Tooltip title="This customer needs a revised order sent to them">
                                              <PendingActions htmlColor={statusCaution}/>
                                            </Tooltip>
                                        
                                            }
                                            { n.accountStatus === 0 && n.customerStatus !== 2 && !(n.floatProblem === 1 || n.declineCount > 0) && n.soStatus === 2 &&
                                            
                                              <Tooltip title="Waiting for this customer to accept an order that has been sent to them">
                                                <HourglassEmpty htmlColor={statusCaution}/>
                                              </Tooltip>
                                        
                                            }
                                            { n.accountStatus === 0 && n.customerStatus !== 2 && !(n.floatProblem === 1 || n.declineCount > 0) && n.soStatus === 0 &&
                                            
                                            <Tooltip title="This customer has to be set up and sent their first order">
                                              <BuildCircle htmlColor={statusBuild}/>
                                            </Tooltip>
                                      
                                          }

                                            { n.accountStatus === 1 &&
                                            
                                                <Tooltip title="This customer is suspended">
                                                    <PauseCircleFilled htmlColor={statusSuspended}/>
                                                </Tooltip>
                                            
                                            }
                                            { n.accountStatus === 3 &&
                                            
                                                <Tooltip title="This customer is cancelled">
                                                    <Cancel htmlColor={statusCancelled}/>
                                                </Tooltip>
                                            
                                            }
                                          </TableCell>
                                      </TableRow>
                                  );
                              })}
                          </TableBody>
                      </Table>
                    </Paper>
                    <div style={AppBindStyles.paginationTableFooter}>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={this.state.rowsPerPage}
                        page={this.state.page}
                        backIconButtonProps={{
                          'aria-label': 'Previous Page',
                        }}
                        nextIconButtonProps={{
                          'aria-label': 'Next Page',
                        }}
                        onPageChange={this.handleChangePage}
                        onRowsPerPageChange={this.handleChangeRowsPerPage}
                      />                        
                    </div>
                  </div>
                )
              }
              return (
                <div>
                  <Typography>Loading...</Typography>
                  <LoadingSpinner/>
                </div>
              );
            }}
          />
        </main>
      );
    } else {
      return (
        <Redirect to='/home' />
      );
    }
  }
}

CustomerSearchTable.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(CustomerSearchTable);
